import CommonAPIService from './CommonHandler';
import _ from 'lodash';
import moment from 'moment';

// 4 Feb 2026 JCH: Keep Home dashboard responsive (performance) in the browser.
// This is a client-side cap (backend accepted >=10k in probe; we cap for memory/DOM).
export const NEWS_QUERY_LIMIT = 1000;
export const CALENDAR_QUERY_LIMIT = 1000;

// 2 Feb 2026 JCH: Home News needs friendly dateRange inputs (2weeks/12months/year:YYYY).
// Normalize UI-friendly date ranges to backend format.
// Accepted inputs:
// - "2weeks" -> "weeks:2"
// - "12months" -> "months:12"
// - "year:YYYY" -> unchanged
// - "weeks:2" / "months:12" / "days:14" -> unchanged
export function normalizeNewsDateRange(dateRange, fallback) {
  if (dateRange === undefined || dateRange === null || dateRange === '') return fallback;

  const raw = String(dateRange).trim().toLowerCase();
  if (!raw) return fallback;

  if (/^year:\d{4}$/.test(raw)) return raw;
  if (/^(days|weeks|months):\d+$/.test(raw)) return raw;

  // Support "2weeks", "12months", etc.
  const m = raw.match(/^(\d+)\s*(day|days|week|weeks|month|months)$/);
  if (m) {
    const n = m[1];
    const unit = m[2].endsWith('s') ? m[2] : `${m[2]}s`;
    return `${unit}:${n}`;
  }

  return raw;
}

function parseRelativeRange(normalizedRange) {
  const m = String(normalizedRange || '').toLowerCase().match(/^(days|weeks|months):(\d+)$/);
  if (!m) return null;
  return { unit: m[1], value: parseInt(m[2], 10) };
}

function needsYearFallback(normalizedRange) {
  const rr = parseRelativeRange(normalizedRange);
  // Backend validation only allows single-digit relative ranges (days/weeks/months: 1-9).
  return !!(rr && rr.value >= 10);
}

function filterNotesByCstampRange(rows, start, end) {
  const safeRows = Array.isArray(rows) ? rows : [];
  return safeRows.filter((n) => {
    if (!n || !n.cstamp) return false;
    const t = moment(n.cstamp);
    if (!t.isValid()) return false;
    return t.isBetween(start, end, undefined, '[]');
  });
}

export class NotesAPIService {
  constructor() {
    this.CommonAPIService = new CommonAPIService();
    this.notesFields = [
      'nid', 'stamp', 'ostamp', 'cstamp', 'fid', 'pid', 'plid', 'direct',
      'type', 'status', 'opener', 'closer', 'signer', 'staff', 'dated', 'due', 're',
      'note', 'frid', 'taken', 'tasks', 'html', 'to'
    ]
  }

  getNote(id, router) {
    const url = `/api/notes/${id}`;
    return this.CommonAPIService.getCall(url, router);
  }

  getNoteFiles(id, router) {
    const fields = 'loid,loidfid,file_description,filename,size,content_type,file_objects.dated,sort_index,f_type';
    const url = `/api/notes/${id}/files?fields=${fields}`;
    return this.CommonAPIService.getCall(url, router);
  }

  connectFiles(id, loids, router) {
    const url = `/api/notes/${id}/files`;
    return this.CommonAPIService.postCall(url, { loids }, router);
  }

  disconnectFiles(id, loids, router) {
    const url = `/api/notes/${id}/files?loids=${loids.join(',')}`;
    return this.CommonAPIService.deleteCall(url, router);
  }

  updateFiles(id, loids, router) {
    const url = `/api/notes/${id}/files`;
    return this.CommonAPIService.putCall(url, loids, router);
  }

  getAllForms(router) {
    const url = '/api/notes/forms';
    return this.CommonAPIService.getCall(url, router);
  }

  getNextNote(userid, router) {
    const url = `/api/notes/jobs/next?userid=${userid}`;
    return this.CommonAPIService.getCall(url, router);
  }

  updateNote(id, note, router) {
    const extraFields = Object.keys(note).filter(key => !this.notesFields.includes(key));
    extraFields.push('dated');// this field is no longer accepted by backend
    note = _.omit(note, extraFields);
    const url = `/api/notes/${id}`;
    return this.CommonAPIService.putCall(url, note, router);
  }

  createNote(note, router) {
    const url = '/api/notes';
    return this.CommonAPIService.postCall(url, note, router);
  }

  deleteNote(id, router) {
    const url = `/api/notes/${id}`;
    return this.CommonAPIService.deleteCall(url, router);
  }

  searchNotes(search, router) {
    const url = '/api/notes/search';
    return this.CommonAPIService.postCall(url, search, router);
  }

  getAllJobs(router) {
    const url = '/api/notes/jobs?job=all';
    return this.CommonAPIService.getCall(url, router);
  }

  /**
   * Get jobs and filter by given parameters
   * @param {object | null} parameters - The query parameters for filtering and ordering the results
   * @param {string} [parameters.usernames] - Comma-separated list of usernames to search for jobs belonging to the given users
   * @param {string} [parameters.job] - The job type to filter by ['users', 'ticklers', 'retries', 'others', 'solicit', 'all'], default is all
   * @param {string} [parameters.status] - The status of the job note to filter by (ex. cancel, xtreme, normal, ...)
   * @param {string} [parameters.type] - The type of the job note to filter by (ex. retry, news, memo, ...)
   * @param {string} [parameters.re] - Looks for notes where re matches or contain the passed re.
   * @param {boolean} [parameters.withACLs] - Default is true, get jobs for other users if the given usernames are in their allow list.
   * @param {Route} router - Object that contains information about the current route.
   * @example { usernames: 'john,doe', job: 'users', status: 'normal', type: 'memo', re: 'test'}
   * @returns {Promise<object[]>} - A promise consisting of an array of job objects matching the specified parameters
   */
  getJobs(parameters, router) {
    const queryParams = new URLSearchParams(parameters ?? {}).toString();
    const url = `/api/notes/jobs?${queryParams}`;
    return this.CommonAPIService.getCall(url, router);
  }

  getTicklersJobs(username, router) {
    const url = `/api/notes/jobs?usernames=${username}&job=ticklers`;
    return this.CommonAPIService.getCall(url, router);
  }

  getRetriesJobs(username, router) {
    const url = `/api/notes/jobs?usernames=${username}&job=retries`;
    return this.CommonAPIService.getCall(url, router);
  }

  getOthersJobs(username, router) {
    const url = `/api/notes/jobs?usernames=${username}&job=others`;
    return this.CommonAPIService.getCall(url, router);
  }

  getSolicitJobs(router) {
    const url = `/api/notes/jobs?job=solicit`;
    return this.CommonAPIService.getCall(url, router);
  }

  getNotesRecipients(nid, router, fields = {firms: '', people : ''
                     , contacts : '', recipients : ''}) {
    let query = `firms_fields=${fields.firms}&people_fields=${fields.people}`;
    query += `&contacts_fields=${fields.contacts}&recipients_fields=${fields.recipients}`
    const url = `/api/notes/${nid}/recipients?${query}`;
    return this.CommonAPIService.getCall((url, router));
  }

  getJobsForUsernames(usernamesArr, router) {
    const username = usernamesArr.join(',');
    const url = `/api/notes/jobs?username=${username}`;
    return this.CommonAPIService.getCall(url, router);
  }

  updateNoteAsRead(nid, router) {
    const readersUrl = `/api/notes/${nid}/readers`;
    return this.CommonAPIService.postCall(readersUrl, {}, router);
  }

  getRead(nid, router) {
    const fields = 're,readers.stamp,readers.uname,status,uname,ostamp,cstamp,staff';
    const url = `/api/notes?fields=${fields}&nid=${nid}`;
    return this.CommonAPIService.getCall(url, router);
  }

  /**
   * Retrieves all news-type notes within a specified date range, sorted by timestamp in descending order.
   *
   * Constructs a URL with query parameters to fetch up to a capped number of notes of type `news`,
   * including various fields and sorting options. It performs a GET request using the CommonAPIService.
   *
   * @param {object} router - The router instance used for API routing or error handling.
   * @param {string} [dateRange='12months'] - "12months" | "year:YYYY" | backend format (e.g. "months:12").
   *
   * @returns {Promise<object>} A promise resolving to the result of the API GET call containing the news notes data.
   */
  getAllNewsNotes(router, dateRange = '12months') {
    const normalizedRange = normalizeNewsDateRange(dateRange, 'months:12');
    // 4 Feb 2026 JCH: Avoid loading full note bodies in list view (performance/memory). Fetch body on expand instead.
    const fields ='cstamp,nid,re,staff,status,type';
    const limit = NEWS_QUERY_LIMIT;
    const orders = 'cstamp';
    const directions = 'DESC';
    // 2 Feb 2026 JCH: Keep querystring style consistent (URLSearchParams caused 400/500 on backend).
    const url = `/api/notes?fields=${fields}&withFirmSort=true&type=news&orders=${orders}&directions=${directions}` +
      `&limit=${limit}&dateRange=${normalizedRange}`;

    // 3 Feb 2026 JCH: backend validates dateRange with a regex that rejects multi-digit relative ranges
    // like months:12; for 12months we fall back to year queries + client-side date filtering.
    const fallbackToYearQueries = async (unit, amount) => {
      const end = moment().endOf('day');
      const start = moment().subtract(amount, unit).startOf('day');
      const years = [];
      for (let y = start.year(); y <= end.year(); y += 1) years.push(y);
      // Query newest year first to support early-capping.
      const yearsDesc = years.slice().sort((a, b) => b - a);
      let combined = [];
      for (let y of yearsDesc) {
        const yrUrl = `/api/notes?fields=${fields}&withFirmSort=true&type=news&orders=${orders}&directions=${directions}` +
          `&limit=${limit}&dateRange=year:${y}`;
        const rows = await this.CommonAPIService.getCall(yrUrl, router);
        if (Array.isArray(rows)) combined = combined.concat(rows);

        const filtered = filterNotesByCstampRange(combined, start, end);
        filtered.sort((a, b) => new Date(b.cstamp) - new Date(a.cstamp));
        const uniq = _.uniqBy(filtered, 'nid');
        if (uniq.length >= limit) return uniq.slice(0, limit);
      }

      const filtered = filterNotesByCstampRange(combined, start, end);
      filtered.sort((a, b) => new Date(b.cstamp) - new Date(a.cstamp));
      return _.uniqBy(filtered, 'nid').slice(0, limit);
    };

    if (needsYearFallback(normalizedRange)) {
      const rr = parseRelativeRange(normalizedRange);
      if (rr) return fallbackToYearQueries(rr.unit, rr.value);
    }

    return this.CommonAPIService.getCall(url, router).catch((e) => {
      const status = e?.response?.status;
      const apiStatus = e?.response?.data?.status;
      const rr = parseRelativeRange(normalizedRange);
      if (status === 400 && apiStatus === 'FAILED_VALIDATION' && rr && rr.value >= 10) {
        return fallbackToYearQueries(rr.unit, rr.value);
      }
      return Promise.reject(e);
    });
  }

  /**
   * Fetches news-type notes associated with a specific staff username within a given date range.
   *
   * Constructs a URL with query parameters to filter notes by type (`news`),
   * staff username, and a specified date range. It then performs a GET request
   * using the CommonAPIService.
   *
   * @param {string} username - The staff username to filter notes by.
   * @param {object} router - The router instance used for API routing or error handling.
   * @param {string} [dateRange='2weeks'] - "2weeks" (My News default) or backend format (e.g. "weeks:2").
   *
   * @returns {Promise<object[]>} A promise resolving to an array of unread "My News" notes.
   */
  getNewsNotesByUsername(username, router, dateRange = '2weeks') {
    const normalizedRange = normalizeNewsDateRange(dateRange, 'weeks:2');
    // 4 Feb 2026 JCH: Avoid loading full note bodies in list view (performance/memory). Fetch body on expand instead.
    const fields = 'cstamp,nid,re,staff,status,type';
    const limit = NEWS_QUERY_LIMIT;
    const orders = 'cstamp';
    const directions = 'DESC';

    // Backend returns unread-only for this query signature; after marking read, refresh so the item disappears.
    // Include news where staff === username OR staff === '' (blank staff).
    const url = `/api/notes?fields=${fields}&withReaders=true&withFirmSort=true&type=news` +
      `&orders=${orders}&directions=${directions}&limit=${limit}&dateRange=${normalizedRange}` +
      `&staff=${encodeURIComponent(username)}&staff=`;

    // If relative range is >=10 (e.g. months:12), backend rejects; fall back to year queries + client filtering by cstamp.
    const fallbackToYearQueries = async (unit, amount) => {
      const end = moment().endOf('day');
      const start = moment().subtract(amount, unit).startOf('day');
      const years = [];
      for (let y = start.year(); y <= end.year(); y += 1) years.push(y);
      const yearsDesc = years.slice().sort((a, b) => b - a);

      let combined = [];
      for (let y of yearsDesc) {
        const yrUrl = `/api/notes?fields=${fields}&withReaders=true&withFirmSort=true&type=news` +
          `&orders=${orders}&directions=${directions}&limit=${limit}&dateRange=year:${y}` +
          `&staff=${encodeURIComponent(username)}&staff=`;
        const rows = await this.CommonAPIService.getCall(yrUrl, router);
        if (Array.isArray(rows)) combined = combined.concat(rows);

        const filtered = filterNotesByCstampRange(combined, start, end);
        filtered.sort((a, b) => new Date(b.cstamp) - new Date(a.cstamp));
        const uniq = _.uniqBy(filtered, 'nid');
        if (uniq.length >= limit) return uniq.slice(0, limit);
      }

      const filtered = filterNotesByCstampRange(combined, start, end);
      filtered.sort((a, b) => new Date(b.cstamp) - new Date(a.cstamp));
      return _.uniqBy(filtered, 'nid').slice(0, limit);
    };

    if (needsYearFallback(normalizedRange)) {
      const rr = parseRelativeRange(normalizedRange);
      if (rr) return fallbackToYearQueries(rr.unit, rr.value);
    }

    return this.CommonAPIService.getCall(url, router).catch((e) => {
      const status = e?.response?.status;
      const apiStatus = e?.response?.data?.status;
      const rr = parseRelativeRange(normalizedRange);
      if (status === 400 && apiStatus === 'FAILED_VALIDATION' && rr && rr.value >= 10) {
        // Same fallback as above, but triggered after a backend validation failure.
        return fallbackToYearQueries(rr.unit, rr.value);
      }
      return Promise.reject(e);
    });
  }

  /**
   * Retrieves calendar notes filtered by type
   * @param {string} type - The type of calendar notes to retrieve (appt | event)
   * @param {object} router - Router object for API call routing
   * @param {string|string[]} [staff] - Optional staff filter(s). Omit for all staff.
   * @returns {Promise} Promise object representing the API call result
   */
  getCalendarNotesByType(type, router, staff = undefined) {
   const params = new URLSearchParams({
     // 4 Feb 2026 JCH: Avoid loading full note bodies in list view (performance/memory). Fetch body on expand instead.
     fields: 'cstamp,nid,re,staff,status,type',
     type: type,
     orders: 'cstamp',
     limit: String(CALENDAR_QUERY_LIMIT),
   });

    if (staff !== undefined && staff !== null) {
      if (Array.isArray(staff)) {
        for (const s of staff) params.append('staff', s);
      } else {
        params.append('staff', staff);
      }
    }

    const url = `/api/notes?${params.toString()}`;
    return this.CommonAPIService.getCall(url, router);
  }

  /**
   * Returns the count of active jobs and the count of extreme jobs for each active user
   * @param {object | null} parameters - The query parameters for filtering and ordering the results.
   * @param {string} [parameters.staff] - Comma-separated list of staff usernames to filter the job counts.
   * @param {string} [parameters.orders] - Comma-separated list to specify the ordering of the results.
   * @param {string} [parameters.directions] - Comma-separated list to specify the direction of the ordering.
   * @param {Route} router - object that contains information about the current route.
   * @example getJobsCountByUsers({staff: 'user1,user2', orders: 'staff'}, this.$route);
   * @example getJobsCountByUsers(null, this.$route);
   * @returns {Promise<{staff: string, activeJobs: number, extremeJobs: number}[]>} - Returns a promise containing an array
   * of objects with properties `staff`, `activeJobs`, and `extremeJobs`.
   */
  getJobsCountByUsers(parameters, router) {
    const queryParams = new URLSearchParams(parameters ?? {}).toString(); // if parameter is null then default input is {}
    const url = `/api/notes/jobs/count?${queryParams}`;
    return this.CommonAPIService.getCall(url, router);
  }
}

export default NotesAPIService;
